import { zodResolver } from "@hookform/resolvers/zod";
import { ChangeEvent, FunctionComponent, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { faEdit, faEnvelope, faUser } from "@fortawesome/pro-regular-svg-icons";

import { Icon, Loader, Modal } from "@/components";

import { useGetRolesQuery, useUpdateUserMutation } from "@/pages/Private/redux/admin/admin.api";
import { User } from "@/pages/Public/pages/Login/schema/login";
import { hasPermission } from "@/utils";

import { useAppSelector } from "@/redux/hooks";

import { PermissionRoles } from "@/enum";

import FormTextField from "../../Account/components/FormTextField";
import { UpdateUser, UpdateUserForm, UpdateUserFormSchema } from "../schema/update-user";
import FormSelectRole from "./FormSelectRole";

interface EditUserModalProps {
	user: User | null;
	isVisible: boolean;
	handleClose: () => void;
	companyId?: number;
}

export const EditUserModal: FunctionComponent<EditUserModalProps> = ({
	user,
	isVisible,
	handleClose,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`modals.editUser.${key}`);

	const [selectedUser, setSelectedUser] = useState<User | null>(user);
	const currentUserAuth = useAppSelector((state) => state.auth.user);
	const { data: roles } = useGetRolesQuery();
	const [updateUser, { isLoading, error }] = useUpdateUserMutation();

	const initialValues: UpdateUserForm = {
		firstName: user?.firstName ?? "",
		lastName: user?.lastName ?? "",
		email: user?.email ?? "",
		roleId: user?.role?.id ?? 0,
	};

	const {
		register,
		handleSubmit,
		setValue,
		formState: { errors },
	} = useForm<UpdateUserForm>({
		defaultValues: initialValues,
		resolver: zodResolver(UpdateUserFormSchema),
	});

	const handleUserRoleChange = (event: ChangeEvent<{ value: unknown }>) => {
		if (roles && event?.target?.value) {
			const selectedRole = roles.data.find((item) => item.id === (event.target.value as number));

			if (selectedUser && selectedRole) {
				setSelectedUser({ ...selectedUser, role: selectedRole });
			}
		}

		setValue("roleId", event.target.value as number);
	};

	const onSubmit = async (values: UpdateUserForm) => {
		const body = {} as Partial<UpdateUser>;
		const { ...info } = values;

		for (const key in info) {
			const initialValue = initialValues[key as keyof UpdateUser];
			const currentValue = values[key as keyof UpdateUser];

			if (currentValue !== initialValue) {
				if (key === "roleId" && user?.role?.id !== currentValue) {
					body.roleId = currentValue as number;
				} else if (currentValue !== undefined && key !== "roleId") {
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(body as any)[key] = currentValue;
				}
			}
		}

		try {
			if (Object.entries(body).length > 0) {
				await updateUser({ id: selectedUser?.id ?? 0, body });
			}

			handleClose();
		} catch (err) {
			console.error(err);
		}
	};

	useEffect(() => {
		if (user) {
			setSelectedUser(user);
		}
	}, [user]);

	const isEnoughPermissionForChangingRole = useMemo(() => {
		if (currentUserAuth && user?.role?.code) {
			return hasPermission(currentUserAuth, [user.role.code as PermissionRoles]);
		}

		return false;
	}, [currentUserAuth, user]);

	return (
		<Modal
			aboveHeader={<Icon className="w-4 h-4" icon={faEdit} />}
			dialogContentClassName="!p-[2px]"
			formError={error}
			handleClose={handleClose}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isVisible}
			subtitle={ts("subtitle")}
			title={ts("title")}
		>
			{!roles ? (
				<Loader />
			) : (
				<>
					<FormTextField
						errorMessage={errors?.firstName?.message}
						formRegister={register("firstName")}
						label={t("basics.firstName")}
						startAdornment={
							<Icon className="w-4 h-4 mr-2 text-primary-gray-lighter" icon={faUser} />
						}
					/>
					<FormTextField
						errorMessage={errors?.lastName?.message}
						formRegister={register("lastName")}
						label={t("basics.lastName")}
						startAdornment={
							<Icon className="w-4 h-4 mr-2 text-primary-gray-lighter" icon={faUser} />
						}
					/>
					<FormTextField
						errorMessage={errors?.email?.message}
						formRegister={register("email")}
						label={t("basics.email")}
						startAdornment={
							<Icon className="w-4 h-4 mr-2 text-primary-gray-lighter" icon={faEnvelope} />
						}
					/>
					<FormSelectRole
						errorMessage={errors?.email?.message}
						handleUserRoleChange={handleUserRoleChange}
						isEnoughPermissionForChangingRole={isEnoughPermissionForChangingRole}
						label={t("basics.role")}
						roles={roles?.data}
						selectedUser={selectedUser}
						user={user}
					/>
				</>
			)}
		</Modal>
	);
};
